import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowLeft, 
    User, 
    Mail, 
    Phone, 
    MapPin, 
    Calendar, 
    Save, 
    CheckCircle, 
    XCircle,
    Key,
    X
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';

const ProfileSettingsPage = ({ lang }) => {
    const navigate = useNavigate();
    const { t } = useTranslation(lang);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        status: "",
        created_at: ""
    });
    
    const [originalData, setOriginalData] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        status: "",
        created_at: ""
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [focusedField, setFocusedField] = useState(null);
    
    // États pour la modale de validation
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [token, setToken] = useState("");
    const [isValidatingToken, setIsValidatingToken] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [isValidationSuccess, setIsValidationSuccess] = useState(false);

    useEffect(() => {
        fetchProfileData();
    }, []);

    const fetchProfileData = async () => {
        try {
            const response = await api.get('/profile');
            const data = response.data;

            if (data.result) {
                const profileData = {
                    name: data.result.full_name || "",
                    email: data.result.email || "",
                    phone: data.result.phone || "",
                    address: data.result.address || "",
                    status: data.result.status || "",
                    created_at: data.result.created_at || ""
                };
                
                setFormData(profileData);
                setOriginalData(profileData);
            }
        } catch (error) {
            console.error(t('errorFetchingProfile'), error);

        } finally {
            setLoadingProfile(false);
        }
    };

      const handlePhoneChange = (value) => {
    // Permettre uniquement les chiffres, espaces, +, (), -
    const cleanedValue = value.replace(/[^\d\s\+\-\(\)]/g, '');
    handleChange('phone', cleanedValue);
  };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const updateData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                address: formData.address
            };
            
            
            const response = await api.post('/profile/update', updateData);

            if (response.data) {
                setShowTokenModal(true);
                setValidationMessage(t('tokenEmailSent'));
                setIsValidationSuccess(false);
                setToken("");
            }
        } catch (error) {
            console.error(t('errorUpdatingProfile'), error);
            setValidationMessage(t('updateRequestError'));
            setIsValidationSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTokenValidation = async () => {
        if (!token.trim()) {
            setValidationMessage(t('enterValidationCode'));
            return;
        }

        setIsValidatingToken(true);
        setValidationMessage("");

        try {
            const response = await api.get(`/profile/update/${token}/validate`);

            if (response.status === 200 || response.status === 201 || response.status === 202) {
                setIsValidationSuccess(true);
                setValidationMessage(t('profileUpdatedSuccess'));

                setTimeout(() => {
                  setShowTokenModal(false);
                  setToken("");
                  setValidationMessage("");
                  setIsValidationSuccess(false);
                  navigate('/settings');
                }, 2000);
                    

            } else {
                setIsValidationSuccess(false);
                setValidationMessage(response.data.result?.message || t('invalidValidationCode'));
            }
        } catch (error) {
            console.error(t('errorValidatingToken'), error);
            setIsValidationSuccess(false);
            setValidationMessage(t('validationErrorTryAgain'));
        } finally {
            setIsValidatingToken(false);
        }
    };

    const handleCancelUpdate = () => {
        setShowTokenModal(false);
        setToken("");
        setValidationMessage("");
        setIsValidationSuccess(false);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
    };

    const getInputStyle = (field) => {
        const baseStyle = styles.input;
        const isEditable = ['name', 'email', 'phone', 'address'].includes(field);
        const isDisabled = !isEditable || (isEditable && !isEditing);
        const isFocused = focusedField === field;
        
        let style = { ...baseStyle };
        
        if (isDisabled) {
            style = { ...style, ...styles.inputDisabled };
        }
        
        if (isFocused && isEditable && isEditing) {
            style = { ...style, ...styles.inputFocus };
        }
        
        return style;
    };

    const getStatusBadgeStyle = () => {
        const baseStyle = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
        };

        if (formData.status === 'enabled') {
            return {
                ...baseStyle,
                background: '#d1fae5',
                color: '#065f46'
            };
        } else {
            return {
                ...baseStyle,
                background: '#fef3c7',
                color: '#92400e'
            };
        }
    };

    const styles = {
        container: {
            padding: '24px',
            background: '#f9fafb',
            minHeight: '100vh',
            position: 'relative'
        },
        header: {
            width: '100%',
            maxWidth: '600px',
            marginBottom: '32px'
        },
        backButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            cursor: 'pointer',
            color: '#374151',
            marginBottom: '16px'
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        form_container: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '600px'
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
        },
        formGroup: {
            marginBottom: '20px'
        },
        label: {
            marginBottom: '8px',
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            fontFamily: 'inherit'
        },
        inputDisabled: {
            background: '#f9fafb',
            color: '#6b7280',
            cursor: 'not-allowed'
        },
        inputFocus: {
            borderColor: '#7c3aed',
            boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
            outline: 'none'
        },
        displayValue: {
            padding: '12px 16px',
            background: '#f9fafb',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#6b7280',
            border: '1px solid #e5e7eb'
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '24px'
        },
        primaryButton: {
            padding: '12px 24px',
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        },
        secondaryButton: {
            padding: '12px 24px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        },
        editButton: {
            padding: '12px 24px',
            background: '#f59e0b',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        },
        loadingContainer: {
            textAlign: 'center',
            padding: '40px'
        },
        // Styles pour la modale
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modalContent: {
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
        },
        modalTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        closeButton: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '8px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        },
        tokenInput: {
            width: '100%',
            padding: '14px 16px',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '16px',
            fontFamily: 'monospace',
            letterSpacing: '2px',
            textAlign: 'center',
            marginBottom: '16px',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box'
        },
        tokenInputFocus: {
            borderColor: '#7c3aed',
            boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
            outline: 'none'
        },
        validationMessage: {
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            lineHeight: '1.5'
        },
        successMessage: {
            background: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0'
        },
        errorMessage: {
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #fecaca'
        },
        infoMessage: {
            background: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #bfdbfe'
        },
        modalButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '20px'
        },
        tokenButton: {
            padding: '12px 24px',
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
        },
        tokenButtonDisabled: {
            opacity: 0.6,
            cursor: 'not-allowed'
        }
    };

    if (loadingProfile) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/settings')}
                        onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                        <ArrowLeft size={20} />
                        {t('backToSettings')}
                    </button>

                    <div style={styles.titleContainer}>
                        <User size={32} color="#7c3aed" />
                        <h1 style={styles.title}>{t('profileSettings')}</h1>
                    </div>
                </div>
                
                <div style={styles.loadingContainer}>
                    <p>{t('loadingProfile')}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div style={styles.container}>
                {/* Header */}
                <div style={styles.header}>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/settings')}
                        onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                        <ArrowLeft size={20} />
                        {t('backToSettings')}
                    </button>

                    <div style={styles.titleContainer}>
                        <User size={32} color="#7c3aed" />
                        <h1 style={styles.title}>{t('profileSettings')}</h1>
                    </div>
                </div>

                {/* Formulaire de profil */}
                <div style={styles.form_container}>
                    <div style={styles.section}>
                        <h2 style={{
                            marginBottom: '24px',
                            color: '#1f2937',
                            fontSize: '20px',
                            fontWeight: '600'
                        }}>
                            {t('personalInformation')}
                        </h2>

                        <form onSubmit={handleSubmit}>
                            <div style={styles.formGrid}>
                                {/* Champs modifiables */}
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <User size={16} />
                                        {t('name')}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        style={getInputStyle('name')}
                                        disabled={!isEditing}
                                        onFocus={() => setFocusedField('name')}
                                        onBlur={() => setFocusedField(null)}
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <Mail size={16} />
                                        {t('email')}
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        style={getInputStyle('email')}
                                        disabled={!isEditing}
                                        onFocus={() => setFocusedField('email')}
                                        onBlur={() => setFocusedField(null)}
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <Phone size={16} />
                                        {t('phone')}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                                          maxLength={12}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                                        style={getInputStyle('phone')}
                                        disabled={!isEditing}
                                        onFocus={() => setFocusedField('phone')}
                                        onBlur={() => setFocusedField(null)}
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <MapPin size={16} />
                                        {t('address')}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => handleChange('address', e.target.value)}
                                        style={getInputStyle('address')}
                                        disabled={!isEditing}
                                        onFocus={() => setFocusedField('address')}
                                        onBlur={() => setFocusedField(null)}
                                    />
                                </div>

                                {/* Champs non modifiables (affichage seulement) */}
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        {formData.status === 'enabled' ? <CheckCircle size={16} color="#065f46" /> : <XCircle size={16} color="#92400e" />}
                                        {t('status')}
                                    </label>
                                    <div style={getStatusBadgeStyle()}>
                                        {formData.status === 'enabled' ? t('enabled') : t('disabled')}
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <Calendar size={16} />
                                        {t('createdAt')}
                                    </label>
                                    <div style={styles.displayValue}>
                                        {formData.created_at}
                                    </div>
                                </div>
                            </div>

                            <div style={styles.buttonGroup}>
                                {!isEditing ? (
                                    <button
                                        type="button"
                                        style={styles.editButton}
                                        onClick={handleEdit}
                                        onMouseEnter={(e) => e.target.style.background = '#d97706'}
                                        onMouseLeave={(e) => e.target.style.background = '#f59e0b'}
                                    >
                                        <User size={16} />
                                        {t('editProfile')}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={handleCancel}
                                            onMouseEnter={(e) => e.target.style.background = '#4b5563'}
                                            onMouseLeave={(e) => e.target.style.background = '#6b7280'}
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            style={styles.primaryButton}
                                            disabled={isLoading}
                                            onMouseEnter={(e) => !isLoading && (e.target.style.background = '#6d28d9')}
                                            onMouseLeave={(e) => !isLoading && (e.target.style.background = '#7c3aed')}
                                        >
                                            <Save size={16} />
                                            {isLoading ? t('saving') : t('saveChanges')}
                                        </button>
                                    </>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Modale de validation par token */}
            {showTokenModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>
                                <Key size={24} color="#7c3aed" />
                                {t('validationRequired')}
                            </h2>
                            <button
                                style={styles.closeButton}
                                onClick={handleCancelUpdate}
                                onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                                onMouseLeave={(e) => e.target.style.background = 'transparent'}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {validationMessage && (
                            <div style={{
                                ...styles.validationMessage,
                                ...(isValidationSuccess ? styles.successMessage :
                                    validationMessage.includes(t('error')) ? styles.errorMessage : styles.infoMessage)
                            }}>
                                {validationMessage}
                            </div>
                        )}

                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ marginBottom: '12px', color: '#374151' }}>
                                {t('enterValidationCodeEmail')}
                            </p>
                            <input
                                type="text"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder={t('tokenPlaceholder')}
                                style={{
                                    ...styles.tokenInput,
                                    ...(token ? styles.tokenInputFocus : {})
                                }}
                                disabled={isValidatingToken || isValidationSuccess}
                                onFocus={(e) => {
                                    setFocusedField('token');
                                }}
                                onBlur={(e) => {
                                    setFocusedField(null);
                                }}
                            />
                        </div>

                        <div style={styles.modalButtons}>
                            <button
                                style={styles.secondaryButton}
                                onClick={handleCancelUpdate}
                                disabled={isValidatingToken}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                style={{
                                    ...styles.tokenButton,
                                    ...((isValidatingToken || isValidationSuccess) && styles.tokenButtonDisabled)
                                }}
                                onClick={() => {handleTokenValidation();
                                    }
                                }
                                disabled={isValidatingToken || isValidationSuccess}
                                onMouseEnter={(e) => !isValidatingToken && !isValidationSuccess && (e.target.style.background = '#6d28d9')}
                                onMouseLeave={(e) => !isValidatingToken && !isValidationSuccess && (e.target.style.background = '#7c3aed')}
                            >
                                {isValidatingToken ? (
                                    <>{t('validating')}</>
                                ) : isValidationSuccess ? (
                                    <>
                                        <CheckCircle size={16} />
                                        {t('validated')}
                                    </>
                                ) : (
                                    <>
                                        <Key size={16} />
                                        {t('validateCode')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProfileSettingsPage;